import { useMemo, useState } from 'react';
import { useCallable, CallStatus } from '../lib/useCallable';

/**
 * The candidate form's questions, editable.
 *
 * Field Builder for this form, and deliberately narrower than the intake one. What it can do is
 * bounded by what is SAFE to change on a form whose answers are already stored and already
 * printed on a legal record:
 *
 *   reword          label, help text — the wording of a disclosure question is the firm's
 *   requirement     required / explain-if-blank / optional
 *   hide            take a shipped question off the form, without deleting its path
 *   reorder         move a question within its section
 *   relabel columns a repeatable's column headings
 *   add             a whole new question, in `custom.*`
 *
 * What it deliberately cannot do is change a question's TYPE or PATH. Answers are stored against
 * both, so renaming a path would orphan every answer already given to it while the form carried
 * on looking correct. For the same reason a shipped question is hidden rather than deleted; only
 * a firm-authored one can be removed, because nothing shipped depends on it.
 *
 * A repeatable's columns can be relabelled but not added to or removed: the PDF renders them as a
 * table sized by their count, and each column's answers live under its own path.
 *
 * Everything here writes one document, `settings/candidateForm`, which the public endpoint serves
 * to the candidate's page and which the PDF and the server-side validation both read. So a change
 * saved here takes effect on the form, the record and the validation at once, with no deploy.
 */

const input =
  'rounded border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900';

const REQUIREMENTS = [
  ['required', 'Required'],
  ['soft_required', 'Ask why, if left blank'],
  ['optional', 'Optional'],
];

const TYPES = [
  ['text', 'Short text'],
  ['textarea', 'Long text'],
  ['yesno', 'Yes / No'],
  ['select', 'Choose one'],
  ['date', 'Date'],
];

const plain = (value) => value && typeof value === 'object' && !Array.isArray(value);

export default function CandidateFormBuilder({ settings, sections, docId = 'candidateForm', heading = 'Edit the questions' }) {
  const { run, busy, error, saved } = useCallable();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  /**
   * The whole document is the editing unit, because adminUpdateSettings REPLACES it. Sending
   * only the part that changed would silently delete the recipients, the sender and everything
   * else configured on the same document.
   */
  const stored = useMemo(
    () => ({
      overrides: plain(settings && settings.overrides) ? settings.overrides : {},
      customFields: plain(settings && settings.customFields) ? settings.customFields : {},
      sectionOverrides: plain(settings && settings.sectionOverrides) ? settings.sectionOverrides : {},
    }),
    [settings]
  );
  const value = draft || stored;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  const patchOverride = (path, patch) =>
    setDraft({
      ...value,
      overrides: { ...value.overrides, [path]: { ...(value.overrides[path] || {}), ...patch } },
    });

  /**
   * A section's heading and the paragraph under it.
   *
   * Kept in its own map rather than in `overrides`, which is keyed by field PATH — a section id
   * and a field path only share a namespace by luck, and the day one collided would be a bad day.
   */
  const patchSection = (id, patch) =>
    setDraft({
      ...value,
      sectionOverrides: {
        ...(value.sectionOverrides || {}),
        [id]: { ...((value.sectionOverrides || {})[id] || {}), ...patch },
      },
    });

  const patchCustom = (id, patch) =>
    setDraft({
      ...value,
      customFields: { ...value.customFields, [id]: { ...(value.customFields[id] || {}), ...patch } },
    });

  const removeCustom = (id) => {
    const next = { ...value.customFields };
    delete next[id];
    const overrides = { ...value.overrides };
    delete overrides[`custom.${id}`];
    setDraft({ ...value, customFields: next, overrides });
  };

  const addCustom = (sectionId) => {
    // Time-based, like every other admin-authored id in this app, so two people adding a question
    // at once cannot collide.
    const id = `q${Date.now().toString(36)}`;
    patchCustom(id, { label: '', type: 'text', requirement: 'optional', sectionId });
    setOpen(true);
  };

  const save = async () => {
    /**
     * Everything this editor owns, over everything else on the document.
     *
     * `value` IS the editable subset, so spreading it means a key added to `stored` above is
     * saved without anybody remembering to add it here too. Listing the keys by hand is what went
     * wrong when section headings were added: the editor wrote them into the draft, the save
     * named only `overrides` and `customFields`, and pressing Save made the change vanish.
     *
     * The `settings` spread still carries every OTHER key — recipients, sender, links — through
     * untouched, because adminUpdateSettings REPLACES the document.
     */
    const result = await run('adminUpdateSettings', {
      docId,
      value: { ...(settings || {}), ...value },
    });
    if (result !== null) setDraft(null);
  };

  /** Reorder by rewriting the whole visible list's orders, so one move cannot leave a tie. */
  const move = (fields, index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const reordered = [...fields];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const overrides = { ...value.overrides };
    reordered.forEach((field, i) => {
      overrides[field.path] = { ...(overrides[field.path] || {}), order: i };
    });
    setDraft({ ...value, overrides });
  };

  const customIdOf = (path) => (path.startsWith('custom.') ? path.slice('custom.'.length) : null);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{heading}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{open ? 'Hide' : 'Edit'}</span>
      </button>

      {open ? (
        <div className="mt-3 space-y-5">
          <p className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Wording, requirement level and order are yours to change, and so is adding a question.
            A question&rsquo;s <strong>type cannot change</strong> and a shipped one cannot be
            deleted — answers are already stored against both, and renaming or removing a question
            would orphan the answers given to it. Hide it instead. Saving takes effect
            immediately, on the form, on the PDF and on what the server will accept.
          </p>

          {sections.map((section) => (
            <div key={section.id}>
              {/*
                The heading and its paragraph, editable. Before this the only editable text near
                the top of a section was a question's LABEL, so an instruction meant for the whole
                section ended up inside one — "***ALL FIELDS MUST BE COMPLETED***" written into the
                label of a table.
              */}
              <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950">
                <label className="block text-xs text-slate-600 dark:text-slate-300">
                  Section heading
                  <input
                    className={`${input} mt-1 w-full font-medium`}
                    value={(value.sectionOverrides || {})[section.id]?.title ?? section.title}
                    onChange={(e) => patchSection(section.id, { title: e.target.value })}
                  />
                </label>
                <label className="mt-2 block text-xs text-slate-600 dark:text-slate-300">
                  Paragraph under the heading
                  <textarea
                    className={`${input} mt-1 w-full`}
                    rows={3}
                    placeholder="Optional"
                    value={
                      (value.sectionOverrides || {})[section.id]?.description ?? section.description ?? ''
                    }
                    onChange={(e) => patchSection(section.id, { description: e.target.value })}
                  />
                </label>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  Put a word in <strong>bold</strong> with **two asterisks**, and press Enter for a
                  new line. Works here and in any question&rsquo;s help text.
                </p>
              </div>
              <div className="mt-2 space-y-2">
                {section.fields
                  .filter((field) => (value.overrides[field.path] || {}).hidden !== true)
                  .map((field, index, shown) => {
                  const override = value.overrides[field.path] || {};
                  const customId = customIdOf(field.path);
                  const custom = customId ? value.customFields[customId] : null;
                  return (
                    <div
                      key={field.path}
                      className="rounded border border-slate-200 p-3 dark:border-slate-700"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <label className="block text-xs text-slate-600 dark:text-slate-300">
                            Question
                            <textarea
                              className={`${input} mt-1 w-full`}
                              rows={2}
                              value={custom ? custom.label || '' : override.label ?? field.label}
                              onChange={(e) =>
                                custom
                                  ? patchCustom(customId, { label: e.target.value })
                                  : patchOverride(field.path, { label: e.target.value })
                              }
                            />
                          </label>
                          <label className="block text-xs text-slate-600 dark:text-slate-300">
                            Help text
                            <input
                              className={`${input} mt-1 w-full`}
                              placeholder="Optional"
                              value={custom ? custom.helpText || '' : override.helpText ?? field.helpText ?? ''}
                              onChange={(e) =>
                                custom
                                  ? patchCustom(customId, { helpText: e.target.value })
                                  : patchOverride(field.path, { helpText: e.target.value })
                              }
                            />
                          </label>
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className={input}
                              value={
                                custom
                                  ? custom.requirement || 'optional'
                                  : override.requirement ?? field.requirement ?? 'optional'
                              }
                              onChange={(e) =>
                                custom
                                  ? patchCustom(customId, { requirement: e.target.value })
                                  : patchOverride(field.path, { requirement: e.target.value })
                              }
                            >
                              {REQUIREMENTS.map(([v, l]) => (
                                <option key={v} value={v}>
                                  {l}
                                </option>
                              ))}
                            </select>

                            {custom ? (
                              <select
                                className={input}
                                value={custom.type || 'text'}
                                onChange={(e) => patchCustom(customId, { type: e.target.value })}
                              >
                                {TYPES.map(([v, l]) => (
                                  <option key={v} value={v}>
                                    {l}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              // Shown, not offered: the reason it cannot change is worth knowing.
                              <span
                                className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                title="A question's type cannot change — answers are stored against it"
                              >
                                {(TYPES.find(([v]) => v === field.type) || [null, field.type])[1]}
                              </span>
                            )}

                            <span className="ml-auto flex items-center gap-1">
                              <button
                                type="button"
                                className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-slate-600"
                                onClick={() => move(shown, index, -1)}
                                disabled={index === 0}
                                aria-label="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-slate-600"
                                onClick={() => move(shown, index, 1)}
                                disabled={index === shown.length - 1}
                                aria-label="Move down"
                              >
                                ↓
                              </button>
                              {custom ? (
                                <button
                                  type="button"
                                  className="text-xs text-red-600 hover:underline dark:text-red-400"
                                  onClick={() => removeCustom(customId)}
                                >
                                  Delete
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="text-xs text-slate-500 hover:underline dark:text-slate-400"
                                  onClick={() => patchOverride(field.path, { hidden: true })}
                                >
                                  Hide
                                </button>
                              )}
                            </span>
                          </div>

                          {/* A dropdown's own choices, one per line — the simplest editor that
                              cannot produce a half-built option. */}
                          {custom && custom.type === 'select' ? (
                            <label className="block text-xs text-slate-600 dark:text-slate-300">
                              Choices, one per line
                              <textarea
                                className={`${input} mt-1 w-full`}
                                rows={3}
                                value={(custom.options || []).map((o) => o.label || o.value).join('\n')}
                                onChange={(e) =>
                                  patchCustom(customId, {
                                    options: e.target.value
                                      .split('\n')
                                      .map((line) => line.trim())
                                      .filter(Boolean)
                                      .map((line) => ({ value: line, label: line })),
                                  })
                                }
                              />
                            </label>
                          ) : null}

                          {/* A repeatable's column headings. Count is fixed — see the header. */}
                          {field.type === 'repeatable' && Array.isArray(field.fields) ? (
                            <div className="rounded bg-slate-50 p-2 dark:bg-slate-800">
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Column headings ({field.fields.length}) — the number of columns is
                                fixed, because each one&rsquo;s answers are stored separately and
                                the PDF lays them out as a table.
                              </p>
                              <div className="mt-1 space-y-1">
                                {field.fields.map((sub) => (
                                  <input
                                    key={sub.path}
                                    className={`${input} w-full`}
                                    value={(override.columns || {})[sub.path] ?? sub.label}
                                    onChange={(e) =>
                                      patchOverride(field.path, {
                                        columns: { ...(override.columns || {}), [sub.path]: e.target.value },
                                      })
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Hidden questions are listed rather than vanishing, or nobody can bring one
                    back and it looks as though the form lost a question. The server hands them
                    over flagged, so their real label and section are known rather than guessed. */}
                {section.fields
                  .filter((field) => (value.overrides[field.path] || {}).hidden === true)
                  .map((field) => (
                    <div
                      key={field.path}
                      className="flex items-center gap-2 rounded border border-dashed border-slate-300 p-2 text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400"
                    >
                      <span className="flex-1">Hidden: {field.label}</span>
                      <button
                        type="button"
                        className="text-firm-700 hover:underline dark:text-firm-300"
                        onClick={() => patchOverride(field.path, { hidden: false })}
                      >
                        Show again
                      </button>
                    </div>
                  ))}

                <button
                  type="button"
                  className="rounded border border-dashed border-slate-400 px-3 py-1 text-xs text-slate-600 hover:border-firm-500 dark:border-slate-500 dark:text-slate-300"
                  onClick={() => addCustom(section.id)}
                >
                  + Add a question to {section.title}
                </button>
              </div>
            </div>
          ))}

          <div className="sticky bottom-0 flex items-center gap-3 border-t border-slate-200 bg-white py-3 dark:border-slate-700 dark:bg-slate-900">
            <button
              type="button"
              onClick={save}
              disabled={busy || !dirty}
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              Save the questions
            </button>
            {dirty ? (
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="text-xs text-slate-500 underline dark:text-slate-400"
              >
                Discard
              </button>
            ) : null}
            <CallStatus busy={busy} error={error} saved={saved} />
          </div>
        </div>
      ) : null}
    </section>
  );
}

